import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  DollarSign,
  Users,
  Target,
  TrendingUp,
  BarChart3,
  ChevronRight,
  Info,
  Zap,
  CheckCircle,
  AlertCircle,
  BookOpen,
  MessageCircle,
  Trophy,
  Briefcase,
  User,
  Brain,
  ShoppingBag,
  Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ResourcePanelProps {
  selectedTab: 'overview' | 'product' | 'framework' | 'competitors';
  scenario: any;
  competitorMentioned: boolean;
}

export function ResourcePanel({
  selectedTab,
  scenario,
  competitorMentioned
}: ResourcePanelProps) {

  const renderOverview = () => (
    <div className="space-y-3">
      {/* Customer Profile */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <User className="h-4 w-4 text-gray-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-900 mb-1">Customer Profile</h3>
            <p className="text-sm text-gray-700">
              {scenario?.persona?.profile?.name || 'Customer'}
            </p>
            <p className="text-xs text-gray-500">
              {scenario?.persona?.profile?.occupation || 'Professional'}
            </p>
            {scenario?.persona?.background?.workHistory && (
              <p className="text-xs text-gray-500 mt-1.5">
                {scenario?.persona?.background?.workHistory}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Objectives */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Target className="h-4 w-4 text-gray-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Session Objectives</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-gray-400 mr-2">•</span>
                {scenario?.scenario?.objective || 'Build rapport and trust'}
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 mr-2">•</span>
                Identify customer needs
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 mr-2">•</span>
                Present tailored solutions
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Key Considerations */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Brain className="h-4 w-4 text-gray-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Key Considerations</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              {scenario?.persona?.personality?.traits?.map((trait: string, index: number) => (
                <li key={index} className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  {trait}
                </li>
              )) || [
                'Value-focused decision maker',
                'Appreciates detailed information',
                'Prefers consultative approach'
              ].map((trait, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  {trait}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProduct = () => (
    <div className="space-y-3">
      {/* Product Overview */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <ShoppingBag className="h-4 w-4 text-gray-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-900 mb-1">
              {scenario?.scenario?.product?.name || scenario?.product_name}
            </h3>
            <p className="text-xs text-gray-500">
              {scenario?.scenario?.product?.company || scenario?.company_name}
            </p>
            {scenario?.scenario?.product?.description && (
              <p className="text-sm text-gray-600 mt-2">
                {scenario?.scenario?.product?.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
          <Sparkles className="h-4 w-4 text-gray-600 mr-2" />
          Key Features
        </h4>
        <ul className="space-y-1.5 text-sm text-gray-600">
          {(scenario?.scenario?.product?.features || scenario?.product_features || [
            'Advanced analytics dashboard',
            'Real-time collaboration tools',
            'Enterprise-grade security',
            'Seamless integrations'
          ]).map((feature: string, index: number) => (
            <li key={index} className="flex items-start">
              <CheckCircle className="h-3.5 w-3.5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Value Proposition */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
          <TrendingUp className="h-4 w-4 text-gray-600 mr-2" />
          Value Proposition
        </h4>
        <ul className="space-y-1.5 text-sm text-gray-600">
          {(scenario?.scenario?.product?.value_props || [
            'Increase productivity by 40%',
            'Reduce operational costs',
            'Improve customer satisfaction',
            'Scale with your business'
          ]).map((value: string, index: number) => (
            <li key={index} className="flex items-start">
              <span className="text-gray-400 mr-2">→</span>
              {value}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderFramework = () => (
    <div className="space-y-3">
      {/* Sales Framework */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
          <BookOpen className="h-4 w-4 text-gray-600 mr-2" />
          Sales Process
        </h3>
        <div className="space-y-2">
          {(scenario?.scenario?.framework?.steps || scenario?.framework?.steps || [
            { phase: 'Opening', description: 'Build rapport and set agenda' },
            { phase: 'Discovery', description: 'Understand needs and challenges' },
            { phase: 'Presentation', description: 'Present tailored solutions' },
            { phase: 'Handling Objections', description: 'Address concerns effectively' },
            { phase: 'Closing', description: 'Secure commitment and next steps' }
          ]).map((step: any, index: number) => (
            <div
              key={index}
              className="flex items-start space-x-3 p-3 bg-gray-50 rounded-md"
            >
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-xs font-medium text-gray-700 border border-gray-200">
                {index + 1}
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">
                  {step.phase || step.name || `Step ${index + 1}`}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  {step.description || step.details}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Best Practices */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
          <AlertCircle className="h-4 w-4 text-gray-600 mr-2" />
          Best Practices
        </h4>
        <ul className="space-y-1.5 text-sm text-gray-600">
          <li className="flex items-start">
            <span className="text-gray-400 mr-2">✓</span>
            Listen actively and ask open-ended questions
          </li>
          <li className="flex items-start">
            <span className="text-gray-400 mr-2">✓</span>
            Focus on value, not just features
          </li>
          <li className="flex items-start">
            <span className="text-gray-400 mr-2">✓</span>
            Use social proof and success stories
          </li>
          <li className="flex items-start">
            <span className="text-gray-400 mr-2">✓</span>
            Always confirm understanding
          </li>
        </ul>
      </div>
    </div>
  );

  const renderCompetitors = () => (
    <div className="space-y-3">
      {competitorMentioned && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <p className="text-sm font-medium text-blue-800">
              Customer mentioned competitors
            </p>
          </div>
          <p className="text-xs text-blue-600 mt-0.5">
            Use the information below to address their concerns
          </p>
        </div>
      )}

      {scenario?.competitors && scenario.competitors.length > 0 ? (
        scenario.competitors.map((competitor: any, index: number) => (
          <div
            key={index}
            className="border border-gray-200 rounded-lg p-4"
          >
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              {competitor.name}
            </h4>
            {competitor.description && (
              <p className="text-xs text-gray-500 mb-3">
                {competitor.description}
              </p>
            )}

            {/* Strengths */}
            <div className="mb-3">
              <h5 className="text-xs font-medium text-gray-700 mb-1.5">
                Their Strengths
              </h5>
              <ul className="space-y-1 text-sm text-gray-600">
                {(competitor.strengths || []).map((strength: string, idx: number) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-gray-400 mr-2">+</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>

            {/* How to Counter */}
            <div className="bg-gray-50 rounded-md p-3">
              <h5 className="text-xs font-medium text-gray-700 mb-1.5">
                How to Position Against
              </h5>
              <ul className="space-y-1 text-sm text-gray-600">
                {(competitor.counter_points || []).map((point: string, idx: number) => (
                  <li key={idx} className="flex items-start">
                    <ChevronRight className="h-3.5 w-3.5 text-blue-500 mr-1 flex-shrink-0 mt-0.5" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-12">
          <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">
            No competitor information available
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto p-4">
      {selectedTab === 'overview' && renderOverview()}
      {selectedTab === 'product' && renderProduct()}
      {selectedTab === 'framework' && renderFramework()}
      {selectedTab === 'competitors' && renderCompetitors()}
    </div>
  );
}